import type { NodePropertySchema } from '../../../types/node-properties';

/**
 * Access Financials MCP Node Schema
 *
 * MCP (Model Context Protocol) integration with Access Financials
 * Uses JSON-RPC 2.0 to communicate with the Financials MCP server
 * for dynamic AI agent tool discovery and execution
 *
 * Supports two operations:
 * - listTools: Discover available tools from the MCP server
 * - executeTool: Execute a specific tool with arguments
 */

const schema: NodePropertySchema = {
  nodeType: 'access-financials-mcp',
  version: '1.0.0',

  properties: [
    // ==================================================================
    // ENVIRONMENT
    // ==================================================================
    {
      key: 'environment',
      label: 'Environment',
      type: 'options',
      required: true,
      default: 'preprod',
      options: [
        { value: 'preprod', name: 'Pre-Production' },
        { value: 'staging', name: 'Staging' },
        { value: 'demo', name: 'Demo' },
        { value: 'production', name: 'Production' },
      ],
      description: 'Select the Access Financials MCP environment',
    },

    // ==================================================================
    // INSTANCE CONFIGURATION
    // ==================================================================
    {
      key: 'instanceId',
      label: 'Instance ID',
      type: 'string',
      required: true,
      placeholder: 'Select an instance or enter ID',
      description: 'The Financials database instance ID (X-Instance-Id header). Select from available instances or enter manually.',
      modeToggle: {
        enabled: true,
        defaultMode: 'text', // Default to manual entry
        loadOptions: {
          method: 'getInstances',
          nodeType: 'access-financials', // Reuse the existing access-financials provider
          dependsOn: ['environment'],
          cacheResults: true,
          cacheTtl: 300000, // 5 minutes - instances don't change often
          requiresCredential: false, // Uses system credentials, not user-selected
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // ==================================================================
    // TOOL SELECTION
    // ==================================================================
    {
      key: 'selectedTools',
      label: 'Available Tools',
      type: 'string',
      required: false,
      placeholder: 'Click Select to load and choose tools',
      description: 'Select which MCP tools to make available to AI agents. Use "Select" mode to browse tools, or enter tool names manually.',
      modeToggle: {
        enabled: true,
        defaultMode: 'text',
        loadOptions: {
          method: 'getTools',
          dependsOn: ['environment', 'instanceId'],
          cacheResults: true,
          cacheTtl: 60000, // 1 minute - tools may change
          requiresCredential: false,
        },
        textModeLabel: 'Manual',
        dataModeLabel: 'Select',
      },
    },

    // ==================================================================
    // ADVANCED OPTIONS (Settings tab)
    // ==================================================================
    {
      key: 'timeout',
      label: 'Timeout (ms)',
      type: 'number',
      section: 'settings',
      default: 30000,
      description: 'Request timeout in milliseconds',
      typeOptions: {
        minValue: 1000,
        maxValue: 300000,
      },
    },
  ],

  metadata: {
    author: 'Evo E2E Team',
    documentation: 'https://confluence.accessgroup.cloud/display/ACCESSAPI/Financials+MCP',
  },
};

export default schema;
